import React, { useState, useEffect, useMemo } from 'react'
import { FaReply, FaPaperclip, FaPaperPlane, FaTimes } from "react-icons/fa";
import { MwInputFile, MwSelector, MwSpinnerButton, MwTextArea } from '../ui';
import { useCurrent, usePost, usePut } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { getLocalizedStatusOptions, getStatusId, getStatusCategory } from '../../constants/ticket';
import { apiClient as axios } from '../../utils/apiClient';
import { getAttachmentInfo, toAttachmentNamesPayload, uploadSupportFiles } from '../../utils/attachments';
import { refreshPendingActions } from './PendingActionGuard';

const stripHtmlText = (value) =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const readJwtPayload = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
};

const readPersistedUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
        return null;
    }
};

const resolveAuthValue = (user, key) => {
    const persistedUser = readPersistedUser();
    const tokenPayload = readJwtPayload();
    return (
        user?.[key] ??
        user?.user?.[key] ??
        persistedUser?.[key] ??
        persistedUser?.user?.[key] ??
        tokenPayload?.[key] ??
        tokenPayload?.user?.[key]
    );
};

const resolveAuthFlag = (user, key) => isTruthyFlag(resolveAuthValue(user, key));

const resolveAuthRole = (user) => resolveAuthValue(user, 'role');

const SUPER_ADMIN_ONLY_STATUS_GROUPS = ['completed', 'closed'];

const CreateTicketDetails = ({ ticketID, supportSettings, currentStatusValue, statusLockedBySuperAdmin, callBackOnCreated }) => {
    const [t] = useTranslation('global')
    const { user, currentLangId } = useCurrent();
    const { data: resultSubmit, loading: loadingSubmit, postData: submitTicket } = usePost(false);
    const { putData, loading: statusLoading } = usePut();
    const [fileLoading, setFileLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [content, setContent] = useState('');
    const [replyVisibility, setReplyVisibility] = useState('internal');
    const [attchment, setAttchment] = useState([]);
    // الاسم الأصلي لكل ملف مرفوع { [url]: name }.
    const [attachmentNameMap, setAttachmentNameMap] = useState({});
    const [errors, setErrors] = useState({});
    const fileInputId = 'support_ticket_details_attachment_admin';

    const authRole = resolveAuthRole(user);
    const isSuperAdmin = authRole === 'admin';
    const currentStatusId = useMemo(
        () => getStatusId(supportSettings?.statuses, currentStatusValue),
        [supportSettings?.statuses, currentStatusValue]
    );
    const statusOptions = useMemo(
        () => getLocalizedStatusOptions(supportSettings?.statuses, currentLangId),
        [supportSettings?.statuses, currentLangId]
    );
    const currentStatusGroup = getStatusCategory(supportSettings?.statuses, currentStatusValue);
    const isStatusLocked = !isSuperAdmin && SUPER_ADMIN_ONLY_STATUS_GROUPS.includes(currentStatusGroup) && statusLockedBySuperAdmin === true;
    const canCreateInternalReply = authRole === 'admin' || (resolveAuthFlag(user, 'supportAdminEnabled') && resolveAuthFlag(user, 'supportCanViewInternalTickets'));
    const visibilityOptions = useMemo(() => ([
        { label: currentLangId === 'ar' ? 'خارجي - مشاركة مع العميل و فريق العمل' : 'External - Shared with customer and team', value: 'external', lang: currentLangId },
        { label: currentLangId === 'ar' ? 'داخلي - داخل فريق العمل فقط' : 'Internal - Team only', value: 'internal', lang: currentLangId },
    ]), [currentLangId]);
    const selectedVisibility = useMemo(
        () => visibilityOptions.find((opt) => opt.value === replyVisibility) || visibilityOptions[0],
        [replyVisibility, visibilityOptions]
    );

    // الحالة بتبدأ فاضية وإجبارية مع كل رد، عشان محدش ينسى يحدّثها.
    // لو الحالة مقفولة (مكتملة/مغلقة من سوبر أدمن) بنعرض الحالة الحالية ومش بنطلب اختيار.
    const [selectedStatus, setSelectedStatus] = useState(null);
    const lockedStatusOption = useMemo(
        () => (isStatusLocked ? statusOptions.find((opt) => opt.value === currentStatusId) || null : null),
        [currentStatusId, isStatusLocked, statusOptions]
    );
    const statusRequired = !isStatusLocked;
    const isStatusMissing = statusRequired && !selectedStatus?.value;

    const uploadHandler = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setFileLoading(true);
        setErrors((state) => ({ ...state, attachment: undefined }));
        try {
            const uploaded = await uploadSupportFiles(files);
            setAttachmentNameMap((state) => ({ ...state, ...Object.fromEntries(uploaded.map((item) => [item.url, item.name])) }));
            setAttchment((state) => [...(Array.isArray(state) ? state : state ? [state] : []), ...uploaded.map((item) => item.url)]);
            e.target.value = '';
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setFileLoading(false);
        }
    }

    const getAttachmentName = (url) => getAttachmentInfo(url, attachmentNameMap).name;

    const removeAttachment = async (attachmentUrl) => {
        if (!attachmentUrl) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                {
                    data: { keyFile: attachmentUrl },
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    validateStatus: () => true
                }
            );
            if (response?.status >= 200 && response?.status < 300) {
                setAttchment((state) => (Array.isArray(state) ? state : [state]).filter((url) => url !== attachmentUrl));
                setErrors((state) => ({ ...state, attachment: undefined }));
                const input = document.getElementById(fileInputId);
                if (input) {
                    input.value = '';
                }
                return;
            }
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setDeleteLoading(false);
        }
    };



    const submitHandler = async () => {
        setErrors({});
        const contentText = stripHtmlText(content);
        if (!contentText && !attchment.length) {
            setErrors((state) => ({ ...state, content: t('support.tickets.commentRequired') }));
            return;
        }
        if (isStatusMissing) {
            setErrors((state) => ({ ...state, status: currentLangId === 'ar' ? 'اختر حالة التذكرة مع الرد' : 'Select the ticket status for this reply' }));
            return;
        }
        if (fileLoading) {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadInProgress') }));
            return;
        }
        const persistedUserId = (() => {
            try {
                const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
                return (
                    persistedUser?._id ||
                    persistedUser?.id ||
                    persistedUser?.userId ||
                    persistedUser?.user?._id ||
                    persistedUser?.user?.id ||
                    persistedUser?.user?.userId ||
                    null
                );
            } catch {
                return null
            }
        })();
        const tokenUserId = (() => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return null;
                const parts = token.split('.');
                if (parts.length < 2) return null;
                const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
                const json = atob(padded);
                const data = JSON.parse(json);
                return data?._id || data?.id || data?.userId || data?.sub || null;
            } catch {
                return null;
            }
        })();

        const createdById =
            user?._id ||
            user?.id ||
            user?.userId ||
            user?.user?._id ||
            user?.user?.id ||
            user?.user?.userId ||
            persistedUserId ||
            tokenUserId;

        if (!createdById) {
            window.alert('تعذر تحديد المستخدم الحالي. الرجاء تسجيل الدخول مرة أخرى.');
            return;
        }

        try {
            // Update status if changed
            const statusChangeAllowed = isSuperAdmin || !isStatusLocked;
            if (selectedStatus?.value && selectedStatus.value !== currentStatusId && statusChangeAllowed) {
                try {
                    await putData(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${ticketID}/status`, {
                        statusId: selectedStatus.value
                    });
                } catch (statusErr) {
                    console.error('Status update failed:', statusErr);
                    // We continue even if status update fails so the comment can be added
                }
            }

            const _data =
            {
                ticketId: ticketID,
                content: content || null,
                attachments: attchment.length ? attchment : null,
                attachmentNames: toAttachmentNamesPayload(attachmentNameMap, attchment),
                createdBy: createdById,
                visibility: canCreateInternalReply ? (replyVisibility || 'internal') : 'external'
            }
            await submitTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/details`, _data);
        } catch (err) {
            console.error('Submit error:', err);
        }
    }
    useEffect(() => {
        if (resultSubmit) {
            setContent('');
            setAttchment([]);
            setAttachmentNameMap({});
            setSelectedStatus(null);
            callBackOnCreated();
            // التعليق بيفك قفل "إجبار التعليق" لو كان على التذكرة دي.
            refreshPendingActions();
        }
    }, [callBackOnCreated, resultSubmit]);

    return (
        <div className='bg-white p-6 my-6 rounded-2xl shadow-sm border border-slate-100'>
            <div className='pb-4 text-sm text-slate-700 flex items-center justify-between gap-2'>
                <div className='flex items-center gap-2 font-bold'>
                    <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                        <FaReply />
                    </div>
                    {t('support.tickets.addComment')}
                </div>
                <div className='flex items-center gap-3'>
                    {canCreateInternalReply && (
                        <div className='w-80'>
                            <div className="border border-slate-200 bg-slate-50 rounded-xl p-3">
                                <MwSelector
                                    label={currentLangId === 'ar' ? 'نوع الرد' : 'Reply Type'}
                                    initalValue={currentLangId === 'ar' ? 'اختر النوع' : 'Select type'}
                                    dataType='labelValue'
                                    _data={visibilityOptions}
                                    selectedItem={selectedVisibility}
                                    setSelectedItem={(item) => setReplyVisibility(item?.value || 'internal')}
                                    withAddNew={false}
                                />
                            </div>
                        </div>
                    )}
                    <div className='w-56'>
                        <div className={`border rounded-xl p-3 ${errors?.status ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                            <MwSelector
                                label={<>{t('support.tickets.status')}{statusRequired && <span className='text-red-500'> *</span>}</>}
                                initalValue={t('support.tickets.selectStatus')}
                                dataType='labelValue'
                                _data={statusOptions}
                                selectedItem={isStatusLocked ? lockedStatusOption : selectedStatus}
                                setSelectedItem={(item) => {
                                    setSelectedStatus(item);
                                    setErrors((state) => ({ ...state, status: undefined }));
                                }}
                                withAddNew={false}
                                disabled={isStatusLocked}
                                invalid={!!errors?.status}
                                invalidMsg={errors?.status}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative">
                <MwTextArea
                    rows={4}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    invalid={!!errors?.content}
                    invalidMsg={errors?.content}
                    classNameCustom="rounded-xl border-slate-200 focus:border-[#08203f] transition-all resize-none p-4"
                    placeholder={t('support.tickets.typeYourReply')}
                    dir="auto"
                    rich
                />
            </div>

            <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-4'>
                <div className="flex-1 w-full md:w-auto">
                    <MwInputFile
                        label={
                            <div className="flex items-center gap-2 text-slate-500 hover:text-[#08203f] transition-colors cursor-pointer group">
                                <FaPaperclip className="group-hover:rotate-45 transition-transform" />
                                <span>{attchment.length ? `${t('support.tickets.attachments')} (${attchment.length})` : t('support.tickets.attachFile')}</span>
                            </div>
                        }
                        onChangeHandler={uploadHandler}
                        acceptTypes='*/*'
                        id={fileInputId}
                        loading={fileLoading}
                        disabled={fileLoading || deleteLoading}
                        classNameCustom="w-full justify-start"
                        classNameInput="sr-only"
                        multiple
                    />
                    {attchment.map((attachmentUrl) => (
                        <div key={attachmentUrl} className="mt-2 flex items-center justify-between gap-3">
                            <a
                                href={attachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] font-semibold text-slate-600 hover:text-[#08203f] truncate"
                            >
                                {getAttachmentName(attachmentUrl)}
                            </a>
                            <button
                                type="button"
                                onClick={() => removeAttachment(attachmentUrl)}
                                disabled={fileLoading || deleteLoading}
                                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    ))}
                    {errors?.attachment && <div className="mt-2 text-[11px] text-red-500 font-medium">{errors.attachment}</div>}
                    {!errors?.attachment && fileLoading && <div className="mt-2 text-[11px] text-slate-500 font-medium">{t('support.tickets.uploadInProgress')}</div>}
                </div>

                <div className="w-full md:w-auto flex justify-end">
                    {(loadingSubmit || statusLoading) ? (
                        <MwSpinnerButton />
                    ) : (
                        <button
                            onClick={submitHandler}
                            disabled={(!stripHtmlText(content) && !attchment.length) || fileLoading || isStatusMissing}
                            title={isStatusMissing ? (currentLangId === 'ar' ? 'اختر حالة التذكرة أولاً' : 'Select a status first') : undefined}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all shadow-sm ${((!stripHtmlText(content) && !attchment.length) || fileLoading || isStatusMissing) ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'primary-bg-color text-white hover:opacity-90 hover:shadow-md active:scale-95'}`}
                        >
                            <span>{t('support.tickets.sendComment')}</span>
                            <FaPaperPlane className="text-xs" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreateTicketDetails
